import React, { useState } from "react";
import { TouchableOpacity, Text, Animated } from "react-native";

const Button = ({
  title,
  onPress,
  variant = "primary",
  size = "medium",
  style,
  textStyle,
  disabled = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [scaleAnim] = useState(new Animated.Value(1));

  const handleMouseEnter = () => {
    setIsHovered(true);
    Animated.spring(scaleAnim, {
      toValue: 1.05,
      friction: 3,
      tension: 40,
      useNativeDriver: true,
    }).start();
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    Animated.spring(scaleAnim, {
      toValue: 1,
      friction: 3,
      tension: 40,
      useNativeDriver: true,
    }).start();
  };

  const handlePressIn = () => {
    Animated.spring(scaleAnim, {
      toValue: 0.98,
      useNativeDriver: true,
    }).start();
  };

  const handlePressOut = () => {
    Animated.spring(scaleAnim, {
      toValue: isHovered ? 1.05 : 1,
      friction: 3,
      tension: 40,
      useNativeDriver: true,
    }).start();
  };

  const getButtonStyle = () => {
    const baseStyle = {
      paddingVertical: size === "large" ? 16 : size === "small" ? 10 : 14,
      paddingHorizontal: size === "large" ? 48 : size === "small" ? 24 : 36,
      borderRadius: 8,
      alignItems: "center",
      justifyContent: "center",
      shadowColor: "#000",
      shadowOffsetWidth: 0,
      shadowOffsetHeight: isHovered ? 8 : 4,
      shadowOpacity: isHovered ? 0.3 : 0.2,
      shadowRadius: isHovered ? 12 : 8,
      elevation: isHovered ? 8 : 4,
      opacity: disabled ? 0.6 : 1,
      transform: [{ scale: scaleAnim }],
      transition: "all 0.3s ease",
    };

    const variantStyles = {
      primary: {
        background: isHovered
          ? "linear-gradient(135deg, #D81B60 0%, #8E24AA 100%)"
          : "linear-gradient(135deg, #E91E63 0%, #9C27B0 100%)",
        backgroundColor: isHovered ? "#D81B60" : "#E91E63",
      },
      secondary: {
        background: isHovered
          ? "linear-gradient(135deg, #7C3AED 0%, #2563EB 100%)"
          : "linear-gradient(135deg, #8B5CF6 0%, #3B82F6 100%)",
        backgroundColor: isHovered ? "#7C3AED" : "#8B5CF6",
      },
      purple: {
        background: isHovered
          ? "linear-gradient(135deg, #7E22CE 0%, #6D28D9 100%)"
          : "linear-gradient(135deg, #9333EA 0%, #7C3AED 100%)",
        backgroundColor: isHovered ? "#7E22CE" : "#7C3AED",
      },
      outline: {
        backgroundColor: isHovered ? "rgba(233, 30, 99, 0.1)" : "transparent",
        borderWidth: 2,
        borderStyle: "solid",
        borderColor: isHovered ? "#D81B60" : "#E91E63",
      },
      reddish: {
        background: isHovered
          ? "linear-gradient(135deg, #FF1744 0%, #F50057 100%)"
          : "linear-gradient(135deg, #FF4081 0%, #FF1744 100%)",
        backgroundColor: isHovered ? "#FF1744" : "#FF4081",
      },
    };

    return { ...baseStyle, ...variantStyles[variant], ...style };
  };

  const getTextStyle = () => {
    const baseTextStyle = {
      color: variant === "outline" ? "#E91E63" : "#FFFFFF",
      fontSize: size === "large" ? 18 : size === "small" ? 14 : 16,
      fontWeight: "700",
      letterSpacing: 1.5,
      fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
      textTransform: "uppercase",
    };

    return { ...baseTextStyle, ...textStyle };
  };

  return (
    <Animated.View style={{ transform: [{ scale: scaleAnim }] }}>
      <TouchableOpacity
        style={getButtonStyle()}
        onPress={onPress}
        onPressIn={handlePressIn}
        onPressOut={handlePressOut}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        disabled={disabled}
        activeOpacity={0.8}
      >
        <Text style={getTextStyle()}>{title}</Text>
      </TouchableOpacity>
    </Animated.View>
  );
};

export default Button;
